// @ts-strict-ignore
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Parameters, WagoApiClient } from '@shared/service/wago-api.client';
import { ModelConversionService } from '@shared/convert/model-conversion.service';
import { NumberUtils } from '@shared/util/number-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectGroupVO } from '../../../model/vo/project-group.vo';
import { HttpParams } from '@angular/common/http';
import { CopyProjectOperationProperties } from '@project/components/project/copy/copy.component';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ProjectGroupDataService {
  private readonly loadOwnedBaseProjectGroupsUrl: string = 'project-group/base/owned';
  private readonly loadProjectGroupsOwnedByUserUrl: string = 'project-group/owned-by-user';
  private readonly loadDefaultProjectUrl: string = 'project-group/default-project-import';
  private readonly loadBaseOwnedAndDefaultProjectGroupsUrl: string = 'project-group/base/owned-and-default';
  private readonly loadProjectGroupByIdUrl: string = 'project-group/';
  private readonly loadSharedProjectGroupByUserIdUrl: string = 'project-group/shared';
  private readonly loadProjectGroupTreeByIdUrl: string = 'project-group/tree/';
  private readonly searchProjectGroupUrl: string = 'project-group/search';
  private readonly loadMyAssignedProjectGroupUrl = 'project-group/my-assigned';
  private readonly loadFilteredBaseGroupsUrl: string = 'project-group/base/filtered';
  private readonly commentProjectGroupUrl: string = 'project-group/comment';

  constructor(private modelConversionService: ModelConversionService, private wagoApiClient: WagoApiClient) {}

  public loadProjectGroupsOwnedByUser(level: number = 0, importType: string = null): Observable<ProjectGroupVO[]> {
    const parameters: Parameters = this.wagoApiClient.getParameters({});
    parameters.askToElevateUserFromGuestByLoggingIn = true;
    parameters.isErrorHandlingRequired = false;
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('level', level.toString());
    if (importType) {
      httpParams = httpParams.append('importType', importType);
    }
    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get(this.loadProjectGroupsOwnedByUserUrl, requestOptions, parameters)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupsVO(response)));
  }

  public loadOwnedBaseProjectGroups(
    level: number = 0,
    importType: string = null,
    excludeHiddenGroups: boolean = false
  ): Observable<ProjectGroupVO[]> {
    const parameters: Parameters = this.wagoApiClient.getParameters({});
    parameters.askToElevateUserFromGuestByLoggingIn = true;
    parameters.isErrorHandlingRequired = false;
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('level', level.toString());
    httpParams = httpParams.append('excludeHidden', excludeHiddenGroups.toString());
    if (importType) {
      httpParams = httpParams.append('importType', importType);
    }

    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get(this.loadOwnedBaseProjectGroupsUrl, requestOptions, parameters)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupsVO(response)));
  }

  public loadDefaultProjectGroups(): Observable<ProjectGroupVO[]> {
    return this.wagoApiClient.get(this.loadDefaultProjectUrl).pipe(
      map((response) => {
        return this.modelConversionService.convertProjectGroupsVO(response);
      })
    );
  }

  public loadBaseOwnedAndDefaultProjectGroups(level: number = 0): Observable<ProjectGroupVO[]> {
    return this.wagoApiClient
      .get(`${this.loadBaseOwnedAndDefaultProjectGroupsUrl}?level=${level}`)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupsVO(response)));
  }

  public loadFilteredBaseGroups(sharedUserId: number, baseProjectGroupIds: number[] = []): Observable<ProjectGroupVO[]> {
    if (!NumberUtils.isNumber(sharedUserId) || !Number.isInteger(sharedUserId)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('sharedUserId', sharedUserId.toString());
    httpParams = httpParams.append('baseProjectGroupIds', baseProjectGroupIds.toString());

    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get(this.loadFilteredBaseGroupsUrl, requestOptions)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupsVO(response)));
  }

  public loadProjectGroupById(
    groupId: number,
    level: number = 0,
    sharedToUserId: number = null,
    importType: string = null,
    excludeHiddenGroups: boolean = false
  ): Observable<ProjectGroupVO> {
    if (!NumberUtils.isNumber(groupId) || !Number.isInteger(groupId)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('level', level.toString());
    httpParams = httpParams.append('excludeHidden', excludeHiddenGroups.toString());
    if (sharedToUserId) {
      httpParams = httpParams.append('sharedToUserId', sharedToUserId.toString());
    }
    if (importType) {
      httpParams = httpParams.append('importType', importType);
    }

    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get(`${this.loadProjectGroupByIdUrl}${groupId}`, requestOptions)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupVO(response)));
  }

  public loadSharedBaseProjectGroups(userId: number, level: number, importType: string): Observable<ProjectGroupVO> {
    if (!NumberUtils.isNumber(userId) || !Number.isInteger(userId)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('level', level.toString());
    httpParams = httpParams.append('sharedFromUserId', userId.toString());

    if (importType) {
      httpParams = httpParams.append('importType', importType);
    }

    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get(this.loadSharedProjectGroupByUserIdUrl, requestOptions)
      .pipe(map((response) => this.convertAndWrapGroupsInNewGroup(response)));
  }

  private convertAndWrapGroupsInNewGroup(json: any): ProjectGroupVO {
    const groups: ProjectGroupVO[] = this.modelConversionService.convertProjectGroupsVO(json);
    const group = new ProjectGroupVO();
    group.groups = groups;
    group.projects = [];
    return group;
  }
  public searchProjectGroups(
    searchString: string,
    projectsLimit: number = 500,
    includeOnlyOwnedGroups: number = 0,
    importType: string = null,
    includeOptionValues: boolean = false
  ): Observable<ProjectGroupVO[]> {
    if (StringUtils.isEmpty(searchString)) {
      return of([]);
    }

    const requestBody = {searchString, projectsLimit, includeOnlyOwnedGroups, importType, includeOptionValues: includeOptionValues};

    return this.wagoApiClient
      .post(this.searchProjectGroupUrl, requestBody)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupsVO(response)));
  }

  public loadMyAssignedProjectGroup(userId: number, level: number = 1, importType: string = null): Observable<ProjectGroupVO> {
    if (!NumberUtils.isNumber(userId) || !Number.isInteger(userId)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('userId', userId.toString());
    httpParams = httpParams.append('level', level.toString());
    if (importType) {
      httpParams = httpParams.append('importType', importType.toString());
    }
    const requestOptions = {
      params: httpParams,
    };

    return this.wagoApiClient
      .get(this.loadMyAssignedProjectGroupUrl, requestOptions)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupVO(response)));
  }

  public loadPreselectedProjectGroups(
    preselectedGroupId: number,
    includeOnlyOwnedGroups: number = 0,
    importType: string = null
  ): Observable<ProjectGroupVO[]> {
    if (!NumberUtils.isNumber(preselectedGroupId) || !Number.isInteger(preselectedGroupId)) {
      return of([]);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('includeOnlyOwnedGroups', includeOnlyOwnedGroups.toString());
    if (importType) {
      httpParams = httpParams.append('importType', importType);
    }

    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get(`${this.loadProjectGroupByIdUrl}${preselectedGroupId}/preselected`, requestOptions)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupsVO(response)));
  }

  public saveProjectGroup(projectGroupVO: ProjectGroupVO): Observable<ProjectGroupVO> {
    return this.wagoApiClient
      .post(this.loadProjectGroupByIdUrl, projectGroupVO, null, {isErrorHandlingRequired: false})
      .pipe(map((response) => this.modelConversionService.convertProjectGroupVO(response)));
  }

  public saveProjectGroupTree(projectGroupVO: ProjectGroupVO): Observable<any> {
    return this.wagoApiClient
      .post(this.loadProjectGroupTreeByIdUrl, projectGroupVO, null, {isErrorHandlingRequired: false});
  }

  public removeGroupByIdRecursive(projectGroupId: number): Observable<boolean> {
    if (!NumberUtils.isNumber(projectGroupId) || !Number.isInteger(projectGroupId)) {
      return of(false);
    }
    return this.wagoApiClient.delete(`${this.loadProjectGroupByIdUrl}${projectGroupId}/recursive`, null, {
      isErrorHandlingRequired: false,
    });
  }

  public moveProjectGroup(groupId: number, targetGroupId: number, isHidden: boolean): Observable<ProjectGroupVO> {
    if (!NumberUtils.isNumber(groupId) || !Number.isInteger(groupId)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    if (targetGroupId) {
      httpParams = httpParams.append('targetGroupId', targetGroupId.toString());
    }
    httpParams = httpParams.append('isHidden', isHidden.toString());

    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .put(`${this.loadProjectGroupByIdUrl}${groupId}/move`, null, requestOptions, { isErrorHandlingRequired: false })
      .pipe(map((response) => this.modelConversionService.convertProjectGroupVO(response)));
  }

  public copyProjectGroup(
    groupId: number,
    targetGroupId: number,
    copyProperties: CopyProjectOperationProperties,
    newName: string = null,
    isHidden: boolean = false
  ): Observable<ProjectGroupVO> {
    if (!NumberUtils.isNumber(groupId) || !Number.isInteger(groupId)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('copyProjectProperties', copyProperties.copyConfigurationProperties.toString());
    httpParams = httpParams.append('copyGroupProperties', copyProperties.copyProjectProperties.toString());
    httpParams = httpParams.append('newName', newName);
    httpParams = httpParams.append('isHidden', isHidden.toString());
    if (targetGroupId) {
      httpParams = httpParams.append('targetGroupId', targetGroupId.toString());
    }

    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .put(`${this.loadProjectGroupByIdUrl}${groupId}/copy`, null, requestOptions, { isErrorHandlingRequired: false })
      .pipe(map((response) => this.modelConversionService.convertProjectGroupVO(response)));
  }

  public changeProjectGroupOwner(projectGroupId: number, username: string): Observable<ProjectGroupVO> {
    if (!NumberUtils.isNumber(projectGroupId) || !Number.isInteger(projectGroupId)) {
      return of(null);
    }
    return this.wagoApiClient
      .put(this.loadProjectGroupByIdUrl + projectGroupId + '/owner/' + `?username=${username}`, null)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupVO(response)));
  }

  public commentProjectGroup(isArticleProjectGroup: boolean, projectGroup: ProjectGroupVO): void {
    if (isArticleProjectGroup || !projectGroup) {
      return;
    }
    const dto = {
      groupId: projectGroup.id,
      comment: projectGroup.comment,
      internalComment: projectGroup.internalComment,
    };
    this.wagoApiClient.post(this.commentProjectGroupUrl, dto).subscribe();
  }

  public changeProjectGroupVisibility(projectGroupId: number, isHidden: boolean): Observable<boolean> {
    if (!NumberUtils.isNumber(projectGroupId)) {
      return of(false);
    }
    return this.wagoApiClient.put(
      this.loadProjectGroupByIdUrl + projectGroupId + '/visibility' + `?hidden=${isHidden}`,
      null
    );
  }

  public changeProjectGroupPinning(
    projectGroupId: number,
    isPinned: boolean,
    overwriteProjects: boolean,
    temporaryLinkGUIDs: string[] = []
  ): Observable<ProjectGroupVO> {
    if (!NumberUtils.isNumber(projectGroupId)) {
      return of(null);
    }
    let httpParams: HttpParams = new HttpParams();
    httpParams = httpParams.append('value', isPinned.toString());
    httpParams = httpParams.append('overwriteProjects', overwriteProjects.toString());
    httpParams = httpParams.append('guids', temporaryLinkGUIDs.join());
    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .put(this.loadProjectGroupByIdUrl + projectGroupId + '/pinned', null, requestOptions)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupVO(response)));
  }

  public loadPinnedProjectGroups(importType: string = null): Observable<ProjectGroupVO[]> {
    let httpParams: HttpParams = new HttpParams();
    if (importType) {
      httpParams = httpParams.append('importType', importType);
    }
    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get(this.loadProjectGroupByIdUrl + 'pinned', requestOptions)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupsVO(response)));
  }

  public loadRecentlyUsedProjectGroups(importType: string = null): Observable<ProjectGroupVO[]> {
    let httpParams: HttpParams = new HttpParams();
    if (importType) {
      httpParams = httpParams.append('importType', importType);
    }
    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get(this.loadProjectGroupByIdUrl + 'recently-used', requestOptions)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupsVO(response)));
  }

  public loadHiddenProjectGroups(importType: string = null): Observable<ProjectGroupVO[]> {
    let httpParams: HttpParams = new HttpParams();
    if (importType) {
      httpParams = httpParams.append('importType', importType);
    }
    const requestOptions = {
      params: httpParams,
    };
    return this.wagoApiClient
      .get(this.loadProjectGroupByIdUrl + 'hidden', requestOptions)
      .pipe(map((response) => this.modelConversionService.convertProjectGroupsVO(response)));
  }
}
